import { Helmet } from 'react-helmet-async';

interface SEOProps {
  title?: string;
  description?: string;
  image?: string;
  url?: string;
  type?: 'website' | 'article' | 'movie' | 'tv_show' | 'review';
  keywords?: string[];
  author?: string;
  publishedTime?: string;
  modifiedTime?: string;
  section?: string;
  locale?: string;
  videoUrl?: string;
  articleBody?: string;
  rating?: {
    value: number;
    count: number;
    bestRating?: number;
    worstRating?: number;
  };
  canonicalUrl?: string;
  breadcrumbs?: Array<{ name: string; url: string }>;
  alternateLanguages?: Array<{ lang: string; url: string }>;
  movieDetails?: {
    duration?: string;
    genre?: string[];
    releaseDate?: string;
    director?: string;
    actor?: string[];
  };
  organization?: {
    name?: string;
    logo?: string;
    sameAs?: string[];
  };
}

export const SEO: React.FC<SEOProps> = ({
  title = 'Cinédelux - Critiques de Films et Séries',
  description = 'Découvrez les meilleures critiques de films et séries sur Cinédelux. Analyses approfondies, notes et recommandations pour tous les cinéphiles.',
  image = 'https://cinedelux.com/og-image.jpg',
  url = 'https://cinedelux.com',
  type = 'website',
  keywords = ['cinéma', 'films', 'séries', 'critiques', 'avis', 'streaming', 'box office', 'actualités cinéma'],
  author = 'Cinédelux',
  publishedTime,
  modifiedTime,
  section = 'Divertissement',
  locale = 'fr_FR',
  videoUrl,
  articleBody,
  rating,
  canonicalUrl = url,
  breadcrumbs = [],
  alternateLanguages = [],
  movieDetails,
  organization = {
    name: 'Cinédelux',
    logo: 'https://cinedelux.com/logo.png',
    sameAs: [
      'https://facebook.com/cinedelux',
      'https://twitter.com/cinedelux',
      'https://instagram.com/cinedelux'
    ]
  }
}) => {
  const siteTitle = title.includes('Cinédelux') ? title : `${title} | Cinédelux`;
  
  // Structured data for the website
  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'Cinédelux',
    url: 'https://cinedelux.com',
    description,
    publisher: {
      '@type': 'Organization',
      name: organization.name,
      logo: {
        '@type': 'ImageObject',
        url: organization.logo
      },
      sameAs: organization.sameAs
    }
  };

  // Structured data for articles and reviews
  const contentSchema = type === 'website' ? null : {
    '@context': 'https://schema.org',
    '@type': type === 'movie' ? 'Movie' : type === 'tv_show' ? 'TVSeries' : type === 'review' ? 'Review' : 'Article',
    headline: title,
    description,
    image: {
      '@type': 'ImageObject',
      url: image
    },
    author: {
      '@type': 'Organization',
      name: author
    },
    publisher: {
      '@type': 'Organization',
      name: organization.name,
      logo: {
        '@type': 'ImageObject',
        url: organization.logo
      }
    },
    datePublished: publishedTime,
    dateModified: modifiedTime || publishedTime,
    ...(type === 'movie' && movieDetails ? {
      duration: movieDetails.duration,
      genre: movieDetails.genre,
      datePublished: movieDetails.releaseDate,
      director: {
        '@type': 'Person',
        name: movieDetails.director
      },
      actor: movieDetails.actor?.map(name => ({
        '@type': 'Person',
        name
      }))
    } : {}),
    ...(rating ? {
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: rating.value,
        ratingCount: rating.count,
        bestRating: rating.bestRating || 5,
        worstRating: rating.worstRating || 1
      }
    } : {})
  };

  // Structured data for breadcrumbs
  const breadcrumbsSchema = breadcrumbs.length > 0 ? {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbs.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.url
    }))
  } : null;

  return (
    <Helmet>
      {/* Balises meta de base */}
      <title>{siteTitle}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords.join(', ')} />
      <link rel="canonical" href={canonicalUrl} />
      <meta name="robots" content="index, follow" />

      {/* Open Graph */}
      <meta property="og:title" content={siteTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:url" content={url} />
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content="Cinédelux" />
      <meta property="og:locale" content={locale} />
      {videoUrl && <meta property="og:video" content={videoUrl} />}

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={siteTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {/* Article spécifique */}
      {publishedTime && <meta property="article:published_time" content={publishedTime} />}
      {modifiedTime && <meta property="article:modified_time" content={modifiedTime} />}
      {section && <meta property="article:section" content={section} />}
      {author && <meta name="author" content={author} />}

      {/* Langues alternatives */}
      {alternateLanguages.map(({ lang, url }) => (
        <link key={lang} rel="alternate" hrefLang={lang} href={url} />
      ))}

      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(websiteSchema, null, 2)}
      </script>
      {contentSchema && (
        <script type="application/ld+json">
          {JSON.stringify(contentSchema, null, 2)}
        </script>
      )}
      {breadcrumbsSchema && (
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbsSchema, null, 2)}
        </script>
      )}
    </Helmet>
  );
};
